import { formatUserMoney, parseUserAmountMinor, userMarketLabel, userAmountPlaceholder, userAmountError } from '../features/users/utils/userMoney';
import { useCallback, useState } from 'react';
import { systemVersionLabel } from '../features/users/utils/systemVersion';
import { Link } from 'react-router-dom';
import {
  useAdminUserList,
  USER_ROLE_LABELS,
  USER_STATUS_LABELS,
  APPLE_VERIFICATION_LABELS,
} from '../features/users';
import type {
  AdminUserListItemDTO,
  AdminWalletAdjustmentType,
  AppleVerificationStatus,
  BooleanFilterValue,
  UserRole,
  UserStatus,
} from '../features/users';
import * as usersService from '../features/users/services/users.service';
import { formatDateTime } from '../lib/format';
import { useToastStore } from '../store/toast.store';

const STATUS_STYLES: Record<UserStatus, string> = {
  active: 'bg-green-50 text-green-700',
  disabled: 'bg-red-50 text-red-700',
};

const APPLE_STATUS_STYLES: Record<Exclude<AppleVerificationStatus, ''>, string> = {
  verified: 'bg-sky-50 text-sky-700',
  revoked: 'bg-amber-50 text-amber-700',
};

const BOOLEAN_FILTER_LABELS: Record<Exclude<BooleanFilterValue, ''>, string> = {
  true: '是',
  false: '否',
};

type WalletAdjustmentDialogState =
  | { type: 'closed' }
  | { type: AdminWalletAdjustmentType; user: AdminUserListItemDTO };

function WalletAdjustmentDialog({
  state,
  loading,
  onConfirm,
  onCancel,
}: {
  state: WalletAdjustmentDialogState;
  loading: boolean;
  onConfirm: (amountMinor: number, remark: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');

  if (state.type === 'closed') return null;

  const isDebit = state.type === 'debit';
  const handleConfirm = async () => {
    const amountMinor = parseUserAmountMinor(amount, state.user);
    if (!amountMinor || amountMinor <= 0) {
      setError(userAmountError(state.user));
      return;
    }
    const trimmedRemark = remark.trim();
    if (!trimmedRemark) {
      setError('请填写备注');
      return;
    }
    setError('');
    await onConfirm(amountMinor, trimmedRemark);
    setAmount('');
    setRemark('');
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={loading ? undefined : onCancel} />
      <div className="relative mx-4 w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
        <h3 className="text-base font-medium text-gray-900">
          {isDebit ? '扣款' : '加款'} · {state.user.nickname || state.user.email || `#${state.user.id}`}
        </h3>
        <p className="mt-2 text-sm text-gray-500">当前余额 {formatUserMoney(state.user.wallet_balance_minor ?? state.user.wallet_balance_fen, state.user)}</p>
        <div className="mt-4 space-y-3">
          <input
            type="text"
            inputMode={state.user.currency_exponent === 0 ? 'numeric' : 'decimal'}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder={userAmountPlaceholder(state.user)}
            className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <textarea
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
            rows={3}
            placeholder="备注"
            className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
          >
            取消
          </button>
          <button
            onClick={() => void handleConfirm()}
            disabled={loading}
            className={`rounded-md px-4 py-2 text-sm text-white transition-colors disabled:opacity-40 ${
              isDebit ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary/90'
            }`}
          >
            确认{isDebit ? '扣款' : '加款'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UsersPage() {
  const addToast = useToastStore((s) => s.addToast);
  const {
    marketId,
    setMarketId,
    items,
    systemVersionCounts,
    total,
    page,
    pageSize,
    totalPages,
    keyword,
    role,
    status,
    createdFrom,
    createdTo,
    hasInviter,
    appleVerificationStatus,
    riskTagged,
    loading,
    error,
    setKeyword,
    setRole,
    setStatus,
    setCreatedFrom,
    setCreatedTo,
    setHasInviter,
    setAppleVerificationStatus,
    setRiskTagged,
    setPage,
    setPageSize,
    resetFilters,
    reload,
  } = useAdminUserList();
  const [walletDialog, setWalletDialog] = useState<WalletAdjustmentDialogState>({ type: 'closed' });
  const [adjustingWallet, setAdjustingWallet] = useState(false);

  const handleAdjustWallet = useCallback(async (amountMinor: number, remark: string) => {
    if (walletDialog.type === 'closed') return;
    setAdjustingWallet(true);
    try {
      await usersService.adjustAdminUserWallet(walletDialog.user.id, {
        type: walletDialog.type,
        amountMinor,
        remark,
      });
      addToast('success', walletDialog.type === 'credit' ? '加款成功' : '扣款成功');
      setWalletDialog({ type: 'closed' });
      reload();
    } catch {
      addToast('error', walletDialog.type === 'credit' ? '加款失败' : '扣款失败');
    } finally {
      setAdjustingWallet(false);
    }
  }, [addToast, reload, walletDialog]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        管理用户基础资料、邀请关系、Apple 验证状态与风控命中情况，并可进入详情进行账号状态操作。
      </p>

      <section className="rounded-xl border border-gray-200 bg-white p-4" aria-label="系统版本统计">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-gray-900">系统版本统计</h2>
          <span className="text-xs text-gray-500">{loading ? '统计中…' : error ? '统计加载失败' : `当前筛选共 ${total} 位用户`}</span>
        </div>
        <p className="mt-1 text-xs text-gray-500">按当前筛选范围内所有用户的最近上报版本统计，每人计一次；未上报用户需使用新版 App 后更新。</p>
        {!loading && !error && (
          systemVersionCounts.length > 0 ? (
            <div className="mt-3 max-h-64 overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100 text-xs text-gray-500"><th className="py-2 text-left font-medium">系统版本</th><th className="py-2 text-right font-medium">用户数量</th><th className="py-2 text-right font-medium">占比</th></tr></thead>
                <tbody>{systemVersionCounts.map((version) => (
                  <tr key={`${version.platform}:${version.os_version}`} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 text-gray-700">{systemVersionLabel(version)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-900">{version.user_count}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500">{total > 0 ? (version.user_count / total * 100).toFixed(1) : '0.0'}%</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <p className="mt-3 text-sm text-gray-400">暂无统计数据</p>
        )}
      </section>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <select aria-label="地区" value={marketId} onChange={(event) => setMarketId(event.target.value)} className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white">
          <option value="">全部地区</option>
          <option value="CN">中国 · CNY</option>
          <option value="VN">越南 · VND</option>
        </select>
        <input
          type="text"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value.trim())}
          placeholder="搜索用户 ID / 邮箱 / 昵称 / 邀请码"
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <select
          value={role}
          onChange={(event) => setRole(event.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部角色</option>
          {(['user', 'admin'] as UserRole[]).map((value) => (
            <option key={value} value={value}>
              {USER_ROLE_LABELS[value]}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部状态</option>
          {(['active', 'disabled'] as UserStatus[]).map((value) => (
            <option key={value} value={value}>
              {USER_STATUS_LABELS[value]}
            </option>
          ))}
        </select>

        <select
          value={appleVerificationStatus}
          onChange={(event) => setAppleVerificationStatus(event.target.value as AppleVerificationStatus)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部 Apple 验证状态</option>
          {(['verified', 'revoked'] as Exclude<AppleVerificationStatus, ''>[]).map((value) => (
            <option key={value} value={value}>
              {APPLE_VERIFICATION_LABELS[value]}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={createdFrom}
          onChange={(event) => setCreatedFrom(event.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <input
          type="date"
          value={createdTo}
          onChange={(event) => setCreatedTo(event.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <select
          value={hasInviter}
          onChange={(event) => setHasInviter(event.target.value as BooleanFilterValue)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">是否已绑定邀请人</option>
          {(['true', 'false'] as Exclude<BooleanFilterValue, ''>[]).map((value) => (
            <option key={value} value={value}>
              {BOOLEAN_FILTER_LABELS[value]}
            </option>
          ))}
        </select>

        <select
          value={riskTagged}
          onChange={(event) => setRiskTagged(event.target.value as BooleanFilterValue)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">是否命中过风险</option>
          {(['true', 'false'] as Exclude<BooleanFilterValue, ''>[]).map((value) => (
            <option key={value} value={value}>
              {BOOLEAN_FILTER_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={resetFilters}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          重置
        </button>
        <button
          onClick={reload}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          刷新
        </button>
      </div>

      {error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={reload} className="text-sm text-primary hover:underline">
            重试
          </button>
        </div>
      )}

      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">当前筛选条件下暂无用户</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">地区</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500">系统版本</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500">角色</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">邀请关系</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">余额</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Apple 验证</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">风险</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">注册时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">
                          {item.nickname || '-'}
                          <span className="text-gray-400 text-xs ml-1">#{item.id}</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-400 break-all">{item.email || '-'}</div>
                        <div className="mt-1 text-xs text-gray-400">邀请码 {item.invite_code || '-'}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-center text-gray-700">
                        {userMarketLabel(item)}
                      </td>
                      <td className="px-4 py-3 align-top text-center text-gray-700">
                        {systemVersionLabel(item.system_version)}
                      </td>
                      <td className="px-4 py-3 align-top text-center text-gray-700">
                        {USER_ROLE_LABELS[item.role] ?? item.role}
                      </td>
                      <td className="px-4 py-3 align-top text-center">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[item.status]}`}>
                          {USER_STATUS_LABELS[item.status] ?? item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-600">
                        {item.direct_inviter ? (
                          <div className="text-xs">
                            <div className="font-medium text-gray-900">
                              {item.direct_inviter.nickname || '-'}
                              <span className="text-gray-400 ml-1">#{item.direct_inviter.id}</span>
                            </div>
                            <div className="mt-1 break-all text-gray-400">{item.direct_inviter.email || '-'}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">未绑定</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right tabular-nums text-gray-900">
                        {formatUserMoney(item.wallet_balance_minor ?? item.wallet_balance_fen, item)}
                      </td>
                      <td className="px-4 py-3 align-top text-center">
                        {item.apple_verification_status ? (
                          <div className="space-y-1">
                            <span
                              className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                                APPLE_STATUS_STYLES[item.apple_verification_status as Exclude<AppleVerificationStatus, ''>] ?? 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {APPLE_VERIFICATION_LABELS[item.apple_verification_status as Exclude<AppleVerificationStatus, ''>]}
                            </span>
                            <div className="max-w-44 break-all text-xs text-gray-500 mx-auto">
                              {item.apple_account || '-'}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">未上报</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-center">
                        {item.risk_tagged ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                            命中 {item.risk_hit_count}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">正常</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-center text-xs text-gray-500 whitespace-nowrap">
                        {formatDateTime(item.created_at)}
                      </td>
                      <td className="px-4 py-3 align-top text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-3 text-sm">
                          <Link to={`/users/${item.id}`} className="text-primary hover:underline">
                            详情
                          </Link>
                          {item.role === 'user' && (
                            <>
                              <button
                                type="button"
                                onClick={() => setWalletDialog({ type: 'credit', user: item })}
                                className="text-green-600 hover:underline"
                              >
                                加款
                              </button>
                              <button
                                type="button"
                                onClick={() => setWalletDialog({ type: 'debit', user: item })}
                                className="text-red-600 hover:underline"
                              >
                                扣款
                              </button>
                            </>
                          )}
                          <Link to={`/team?userId=${item.id}`} className="text-gray-500 hover:text-gray-700">
                            团队
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <span>共 {total} 条</span>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2">
                <span>每页</span>
                <select
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                  className="h-8 rounded border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {[20, 50, 100, 1000].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
                <span>条</span>
              </label>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <span className="tabular-nums">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </>
      )}
      <WalletAdjustmentDialog
        key={walletDialog.type === 'closed' ? 'closed' : `${walletDialog.user.id}-${walletDialog.type}`}
        state={walletDialog}
        loading={adjustingWallet}
        onConfirm={handleAdjustWallet}
        onCancel={() => setWalletDialog({ type: 'closed' })}
      />
    </div>
  );
}
